import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { isValidEmail, MAX_ATTENDEES, REMINDER_OPTIONS, REPEAT_OPTIONS } from './constants';

const splitDateTime = (value) => {
  const parts = String(value || '').split(' ');

  return { date: parts[0] || '', time: (parts[1] || '').slice(0, 5) };
};

const joinDateTime = (date, time) => `${date} ${time}`;

/**
 * Модалка події календаря: створення / редагування власної події і картка
 * події з розкладу (тільки перегляд — її веде школа).
 */
const CalendarEventModal = ({ mode, event, reminderDefault, saving, error, onSave, onDelete, onClose }) => {
  const readOnly = mode === 'view';

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [repeat, setRepeat] = useState('none');
  const [reminder, setReminder] = useState(reminderDefault);
  const [attendees, setAttendees] = useState([]);
  const [attendeeDraft, setAttendeeDraft] = useState('');
  const [localError, setLocalError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const start = splitDateTime(event?.start_at);
    const end = splitDateTime(event?.end_at);

    setTitle(event?.title || '');
    setDescription(event?.description || '');
    setStartDate(start.date);
    setStartTime(start.time);
    setEndDate(end.date);
    setEndTime(end.time);
    setRepeat(event?.repeat && event.repeat !== 'custom' ? event.repeat : 'none');
    setReminder(
      event?.reminder_minutes === null || event?.reminder_minutes === undefined
        ? reminderDefault
        : event.reminder_minutes
    );
    setAttendees(Array.isArray(event?.attendees) ? event.attendees : []);
    setAttendeeDraft('');
    setLocalError('');
    setConfirmDelete(false);
  }, [event, reminderDefault]);

  // Клас `cal-open` на body ставить сама панель — якщо ним керувати ще й тут,
  // закриття модалки зняло б блокування скролу з відкритої панелі.

  const isCustomRepeat = event?.repeat === 'custom';

  // Панель показує КОЖНЕ входження серії окремим чіпом, а в Google і в базі
  // серія — одна подія. Тому і збереження, і видалення діють на всю серію —
  // про це треба попередити ДО кліку, а не після.
  const isSeries = mode === 'edit' && (event?.is_recurring_instance || (event?.repeat && event.repeat !== 'none'));

  const heading = useMemo(() => {
    if (mode === 'create') {
      return 'Нова подія';
    }

    return mode === 'edit' ? 'Моя подія' : event?.type_label || 'Подія';
  }, [mode, event]);

  const addAttendee = (raw) => {
    const email = String(raw || '').trim().replace(/,$/, '');

    if (!email) {
      return true;
    }

    if (!isValidEmail(email)) {
      setLocalError(`«${email}» не схоже на email.`);

      return false;
    }

    if (attendees.length >= MAX_ATTENDEES) {
      setLocalError(`Можна запросити не більше ${MAX_ATTENDEES} людей.`);

      return false;
    }

    if (!attendees.includes(email)) {
      setAttendees([...attendees, email]);
    }

    setAttendeeDraft('');
    setLocalError('');

    return true;
  };

  const handleAttendeeKeyDown = (domEvent) => {
    if (domEvent.key === 'Enter' || domEvent.key === ',' || domEvent.key === ' ') {
      domEvent.preventDefault();
      addAttendee(attendeeDraft);
    }
  };

  const submit = () => {
    if (!title.trim()) {
      setLocalError('Вкажіть назву події.');

      return;
    }

    if (!startDate || !startTime || !endDate || !endTime) {
      setLocalError('Заповніть дату й час початку та завершення.');

      return;
    }

    if (joinDateTime(endDate, endTime) <= joinDateTime(startDate, startTime)) {
      setLocalError('Час завершення має бути пізніше за час початку.');

      return;
    }

    // Незакінчений email у полі вводу — типова втрата даних: людина ввела адресу
    // і натиснула «Зберегти», не додавши її чипом.
    const pending = attendeeDraft.trim();
    let finalAttendees = attendees;

    if (pending) {
      if (!isValidEmail(pending)) {
        setLocalError(`«${pending}» не схоже на email.`);

        return;
      }

      if (!attendees.includes(pending)) {
        if (attendees.length >= MAX_ATTENDEES) {
          setLocalError(`Можна запросити не більше ${MAX_ATTENDEES} людей.`);

          return;
        }

        finalAttendees = [...attendees, pending];
        setAttendees(finalAttendees);
      }

      setAttendeeDraft('');
    }

    setLocalError('');

    onSave({
      title: title.trim(),
      description: description.trim(),
      start_at: joinDateTime(startDate, startTime),
      end_at: joinDateTime(endDate, endTime),
      repeat,
      attendees: finalAttendees,
      reminder_minutes: reminder,
    });
  };

  const message = localError || error;

  const renderReadOnly = () => (
    <div className="cal-modal__body">
      <div className="cal-modal__badge">Подія з розкладу — редагується школою</div>
      <div className="cal-modal__heading">{event?.title}</div>

      <dl className="cal-modal__kv">
        <dt>Коли</dt>
        <dd>{`${event?.date} · ${event?.start} – ${event?.end}`}</dd>

        {event?.teacher && (
          <>
            <dt>Викладач</dt>
            <dd>{`${event.teacher}${event.is_substitute ? ' (заміна)' : ''}`}</dd>
          </>
        )}

        {event?.group && (
          <>
            <dt>Група</dt>
            <dd>{event.group}</dd>
          </>
        )}

        {event?.direction && (
          <>
            <dt>Напрям</dt>
            <dd>{event.direction}</dd>
          </>
        )}
      </dl>

      <p className="cal-modal__hint">
        Якщо заняття перенесуть або скасують — подія оновиться автоматично, зокрема у вашому Google-календарі.
      </p>
    </div>
  );

  const renderForm = () => (
    <div className="cal-modal__body">
      {isSeries && (
        <p className="cal-note cal-note--warn cal-note--inline">
          Це одне з повторень серії. Зміни й видалення застосуються до <b>всієї серії</b>.
        </p>
      )}

      <div className="cal-field">
        <label htmlFor="cal-title">Назва події</label>
        <input
          id="cal-title"
          className="cal-input"
          type="text"
          value={title}
          maxLength={255}
          placeholder="Наприклад: повторити слова"
          onChange={(domEvent) => setTitle(domEvent.target.value)}
        />
      </div>

      <div className="cal-field">
        <label htmlFor="cal-start-date">Початок</label>
        <div className="cal-field__pair">
          <input
            id="cal-start-date"
            className="cal-input"
            type="date"
            value={startDate}
            onChange={(domEvent) => setStartDate(domEvent.target.value)}
          />
          <input
            className="cal-input"
            type="time"
            aria-label="Час початку"
            value={startTime}
            onChange={(domEvent) => setStartTime(domEvent.target.value)}
          />
        </div>
      </div>

      <div className="cal-field">
        <label htmlFor="cal-end-date">Завершення</label>
        <div className="cal-field__pair">
          <input
            id="cal-end-date"
            className="cal-input"
            type="date"
            value={endDate}
            onChange={(domEvent) => setEndDate(domEvent.target.value)}
          />
          <input
            className="cal-input"
            type="time"
            aria-label="Час завершення"
            value={endTime}
            onChange={(domEvent) => setEndTime(domEvent.target.value)}
          />
        </div>
      </div>

      <div className="cal-field">
        <label htmlFor="cal-description">Опис</label>
        <textarea
          id="cal-description"
          className="cal-input cal-input--area"
          value={description}
          maxLength={1000}
          placeholder="Деталі, посилання, нотатки"
          onChange={(domEvent) => setDescription(domEvent.target.value)}
        />
      </div>

      <div className="cal-field">
        <label htmlFor="cal-repeat">Повторення</label>
        <select
          id="cal-repeat"
          className="cal-input"
          value={repeat}
          onChange={(domEvent) => setRepeat(domEvent.target.value)}
        >
          {REPEAT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        {isCustomRepeat && (
          <p className="cal-field__note">
            Ця подія має нестандартне правило повторення. Якщо зберегти — воно зміниться на обране вище.
          </p>
        )}
      </div>

      <div className="cal-field">
        <label htmlFor="cal-reminder">Нагадати</label>
        <select
          id="cal-reminder"
          className="cal-input"
          value={reminder}
          onChange={(domEvent) => setReminder(Number(domEvent.target.value))}
        >
          {REMINDER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      <div className="cal-field">
        <label htmlFor="cal-attendee">Запросити (email)</label>
        {attendees.length > 0 && (
          <ul className="cal-chips">
            {attendees.map((email) => (
              <li key={email} className="cal-chips__item">
                <span>{email}</span>
                <button
                  type="button"
                  aria-label={`Прибрати ${email}`}
                  onClick={() => setAttendees(attendees.filter((item) => item !== email))}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
        <input
          id="cal-attendee"
          className="cal-input"
          type="email"
          value={attendeeDraft}
          placeholder="name@example.com і Enter"
          onChange={(domEvent) => setAttendeeDraft(domEvent.target.value)}
          onKeyDown={handleAttendeeKeyDown}
          onBlur={() => addAttendee(attendeeDraft)}
        />
        <p className="cal-field__note">Запрошення надійде з вашого Google-акаунта.</p>
      </div>

      <p className="cal-note cal-note--ok">Подія зʼявиться у вашому Google-календарі одразу після збереження.</p>
    </div>
  );

  return (
    <div className="cal-modal">
      <div className="cal-modal__overlay" onClick={onClose} role="presentation"></div>

      <div className="cal-modal__box" role="dialog" aria-modal="true" aria-label={heading}>
        <div className="cal-modal__head">
          <b>{heading}</b>
          <button type="button" className="cal-icon-btn" aria-label="Закрити" onClick={onClose}>✕</button>
        </div>

        {readOnly ? renderReadOnly() : renderForm()}

        {message && <p className="cal-note cal-note--err" role="alert">{message}</p>}

        <div className="cal-modal__foot">
          {mode === 'edit' && !confirmDelete && (
            <button type="button" className="cal-btn cal-btn--danger" disabled={saving} onClick={() => setConfirmDelete(true)}>
              Видалити
            </button>
          )}

          {mode === 'edit' && confirmDelete && (
            <div className="cal-modal__confirm">
              <span>{isSeries ? 'Видалити ВСЮ серію повторень із календаря?' : 'Видалити подію з календаря?'}</span>
              <button type="button" className="cal-btn cal-btn--soft" disabled={saving} onClick={() => setConfirmDelete(false)}>
                Ні
              </button>
              <button type="button" className="cal-btn cal-btn--danger-solid" disabled={saving} onClick={onDelete}>
                Так, видалити
              </button>
            </div>
          )}

          {!confirmDelete && (
            <div className="cal-modal__actions">
              <button type="button" className="cal-btn cal-btn--soft" disabled={saving} onClick={onClose}>
                {readOnly ? 'Закрити' : 'Скасувати'}
              </button>

              {!readOnly && (
                <button type="button" className="cal-btn cal-btn--main" disabled={saving} onClick={submit}>
                  {saving ? 'Зберігаємо…' : (mode === 'create' ? 'Створити' : 'Зберегти')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

CalendarEventModal.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit', 'view']).isRequired,
  event: PropTypes.object,
  reminderDefault: PropTypes.number,
  saving: PropTypes.bool,
  error: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

CalendarEventModal.defaultProps = {
  event: null,
  reminderDefault: 60,
  saving: false,
  error: '',
  onDelete: () => {},
};

export default CalendarEventModal;
